/**
 * Pure clustering for Pay Component Import redesign review.
 * Groups rows by issue type so the UI can render Work Pattern–style tabs
 * and collapsible sections. No React — safe for unit tests.
 */
import type { MappedRow } from "../utils/mappedRows";

export type PayComponentClusterId =
  | "edited"
  | "unknownComponent"
  | "employeeNotFound"
  | "invalidValue"
  | "missingRequired"
  | "duplicate"
  | "assignFailed"
  | "catchAll"
  | "passed";

export type PayComponentClusterKind = "error" | "changed" | "passed";

export type PayComponentTabId = "all" | "errors" | "edited" | "passed";

export interface PayComponentClusterConfig {
  id: PayComponentClusterId;
  kind: PayComponentClusterKind;
  title: string;
  hint: string;
}

const ERROR_CLUSTER_ORDER: PayComponentClusterId[] = [
  "unknownComponent",
  "employeeNotFound",
  "missingRequired",
  "invalidValue",
  "duplicate",
  "assignFailed",
  "catchAll",
];

/** Display order: edited first (needs re-validate), then errors, then passed. */
const CLUSTER_DISPLAY_ORDER: PayComponentClusterId[] = [
  "edited",
  ...ERROR_CLUSTER_ORDER,
  "passed",
];

export const PAY_COMPONENT_CLUSTER_CONFIGS: Record<
  PayComponentClusterId,
  PayComponentClusterConfig
> = {
  edited: {
    id: "edited",
    kind: "changed",
    title: "Edited since validation",
    hint: "These rows changed after the last validation. Re-validate to refresh their checks.",
  },
  unknownComponent: {
    id: "unknownComponent",
    kind: "error",
    title: "Unknown component name",
    hint: "Use a component name that exists for this company, or fix the Component column and re-validate.",
  },
  employeeNotFound: {
    id: "employeeNotFound",
    kind: "error",
    title: "Employee not found",
    hint: "Check the Employee Code matches an employee on this company.",
  },
  missingRequired: {
    id: "missingRequired",
    kind: "error",
    title: "Missing required value",
    hint: "Fill the required field in Edit, then re-validate.",
  },
  invalidValue: {
    id: "invalidValue",
    kind: "error",
    title: "Invalid value",
    hint: "Correct the amount or parameter value, then re-validate.",
  },
  duplicate: {
    id: "duplicate",
    kind: "error",
    title: "Duplicate row",
    hint: "Remove or change the duplicate employee/component combination.",
  },
  assignFailed: {
    id: "assignFailed",
    kind: "error",
    title: "Assignment rejected",
    hint: "Christmas rejected the assignment. Fix the row details or mappings, then re-validate.",
  },
  catchAll: {
    id: "catchAll",
    kind: "error",
    title: "Validation error",
    hint: "Fix the highlighted row, then re-validate.",
  },
  passed: {
    id: "passed",
    kind: "passed",
    title: "Passed",
    hint: "",
  },
};

export interface ClusteredPayComponentRow {
  row: MappedRow;
  clusterId: PayComponentClusterId;
  errorMessages: string[];
}

export interface PayComponentClusterGroup {
  clusterId: PayComponentClusterId;
  config: PayComponentClusterConfig;
  rows: ClusteredPayComponentRow[];
}

export interface PayComponentTabCounts {
  all: number;
  errors: number;
  edited: number;
  passed: number;
}

/** Collapse quoted / specific values so similar messages share a cluster key. */
export function normalizePayComponentErrorMessage(message: string): string {
  return message.replace(/'[^']*'/g, "'…'").replace(/"[^"]*"/g, '"…"');
}

export function classifyPayComponentErrorCluster(
  message: string,
): PayComponentClusterId {
  const lower = message.toLowerCase();
  if (
    /unknown component/.test(lower) ||
    /component .* not (found|recognised|recognized)/.test(lower)
  ) {
    return "unknownComponent";
  }
  if (/employee not found|unknown employee|employee .* not found/.test(lower)) {
    return "employeeNotFound";
  }
  if (
    /required|missing|must (be|have)|blank|empty/.test(lower) &&
    !/amount|money|value|number/.test(lower)
  ) {
    return "missingRequired";
  }
  if (
    /amount|money|not a valid|invalid (value|number|format)|bad value|paycomp_bad_value/.test(
      lower,
    )
  ) {
    return "invalidValue";
  }
  if (/duplicate/.test(lower)) {
    return "duplicate";
  }
  if (
    /paycomp_assign|assign_failed|xem\d+|rejected the (pay element |)?assignment/.test(
      lower,
    )
  ) {
    return "assignFailed";
  }
  return "catchAll";
}

/**
 * When a row has several errors, prefer the most specific cluster in
 * ERROR_CLUSTER_ORDER.
 */
export function classifyPrimaryPayComponentCluster(
  messages: readonly string[],
): PayComponentClusterId {
  if (messages.length === 0) return "passed";
  const ids = messages.map(classifyPayComponentErrorCluster);
  for (const id of ERROR_CLUSTER_ORDER) {
    if (ids.includes(id)) return id;
  }
  return "catchAll";
}

export function clusterPayComponentRows(args: {
  reviewRows: readonly MappedRow[];
  errorsByRow: ReadonlyMap<number, string[]>;
  /** 1-based row indexes edited since the last validation. */
  editedRowIndexes?: ReadonlySet<number> | readonly number[];
}): ClusteredPayComponentRow[] {
  const edited =
    args.editedRowIndexes instanceof Set
      ? args.editedRowIndexes
      : new Set(args.editedRowIndexes ?? []);

  return args.reviewRows.map((row) => {
    const errorMessages = args.errorsByRow.get(row.index) ?? [];
    if (edited.has(row.index)) {
      return {
        row,
        clusterId: "edited" as const,
        errorMessages,
      };
    }
    return {
      row,
      clusterId: classifyPrimaryPayComponentCluster(errorMessages),
      errorMessages,
    };
  });
}

export function groupPayComponentRowsByClusters(
  rows: readonly ClusteredPayComponentRow[],
): PayComponentClusterGroup[] {
  const map = new Map<PayComponentClusterId, ClusteredPayComponentRow[]>();
  for (const row of rows) {
    const list = map.get(row.clusterId) ?? [];
    list.push(row);
    map.set(row.clusterId, list);
  }

  const groups: PayComponentClusterGroup[] = [];
  for (const id of CLUSTER_DISPLAY_ORDER) {
    const clusterRows = map.get(id);
    if (!clusterRows?.length) continue;
    if (id === "passed") {
      const hasBlocking =
        groups.some((g) => g.config.kind === "error") ||
        (map.get("edited")?.length ?? 0) > 0;
      groups.push({
        clusterId: id,
        config: {
          ...PAY_COMPONENT_CLUSTER_CONFIGS.passed,
          title: hasBlocking
            ? `${clusterRows.length} ready to import`
            : `${clusterRows.length} passed`,
        },
        rows: clusterRows,
      });
      continue;
    }
    if (id === "edited") {
      groups.push({
        clusterId: id,
        config: {
          ...PAY_COMPONENT_CLUSTER_CONFIGS.edited,
          title:
            clusterRows.length === 1
              ? "1 row edited since validation"
              : `${clusterRows.length} rows edited since validation`,
        },
        rows: clusterRows,
      });
      continue;
    }
    groups.push({
      clusterId: id,
      config: PAY_COMPONENT_CLUSTER_CONFIGS[id],
      rows: clusterRows,
    });
  }
  return groups;
}

export function countPayComponentTabs(
  rows: readonly ClusteredPayComponentRow[],
): PayComponentTabCounts {
  let errors = 0;
  let edited = 0;
  let passed = 0;
  for (const row of rows) {
    const kind = PAY_COMPONENT_CLUSTER_CONFIGS[row.clusterId].kind;
    if (kind === "error") errors += 1;
    else if (kind === "changed") edited += 1;
    else passed += 1;
  }
  return { all: rows.length, errors, edited, passed };
}

export function getVisiblePayComponentGroups(
  tab: PayComponentTabId,
  groups: readonly PayComponentClusterGroup[],
): PayComponentClusterGroup[] {
  if (tab === "all") return [...groups];
  if (tab === "errors") {
    return groups.filter((g) => g.config.kind === "error");
  }
  if (tab === "edited") {
    return groups.filter((g) => g.config.kind === "changed");
  }
  return groups.filter((g) => g.config.kind === "passed");
}

export function firstPayComponentClusterIdForTab(
  tab: PayComponentTabId,
  groups: readonly PayComponentClusterGroup[],
): PayComponentClusterId | null {
  const visible = getVisiblePayComponentGroups(tab, groups);
  return visible[0]?.clusterId ?? null;
}

export function filterPayComponentGroupsBySearch(
  groups: readonly PayComponentClusterGroup[],
  query: string,
): PayComponentClusterGroup[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [...groups];
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((item) => {
        const haystack = [
          item.row.employeeCode,
          item.row.employeeName,
          item.row.component,
          item.row.startDate ?? "",
          ...item.errorMessages,
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(trimmed);
      }),
    }))
    .filter((group) => group.rows.length > 0);
}

export function formatPayComponentBlockedBanner(
  errorRowCount: number,
  totalRows: number,
): string {
  const valid = totalRows - errorRowCount;
  if (errorRowCount <= 0) {
    return `${totalRows} of ${totalRows} rows passed.`;
  }
  if (valid <= 0) {
    return `${errorRowCount} of ${totalRows} rows have errors. Fix them before importing, or download the errors CSV.`;
  }
  return `${errorRowCount} of ${totalRows} rows have errors. ${valid} row${valid === 1 ? "" : "s"} can still be imported; ${errorRowCount} row${errorRowCount === 1 ? "" : "s"} with errors will be skipped.`;
}

export function addEditedRowIndex(
  current: readonly number[],
  rowIndex: number,
): number[] {
  if (current.includes(rowIndex)) return [...current];
  return [...current, rowIndex].sort((a, b) => a - b);
}
